import { ref, type Ref } from "vue";
import { useAnimationFrame } from "./useAnimationFrame";
import { lerp, clamp } from "math";
import { refWithControl } from "@vueuse/core";

export type UseAnimationStep = {
  current: Ref<number>;
  to: (target: number, duration?: number, curve?: CurveFunction) => Promise<void>;
  set: (value: number) => void;
  stop: () => void;
  running: Ref<boolean>;
};

type CurveFunction = (start: number, target: number, mix: number) => number;

export type UseAnimationStepProps = {
  initial?: number;
  duration?: number;
  curve?: CurveFunction;
  callback?: (value: number) => any;
}

//const isBadNumber = (x: any): boolean => typeof x !== 'number' || isNaN(x) || (!isFinite(x));
//const fixBadNumber = (x: number, fallback: number) => isBadNumber(x) ? fallback : x;

export const useAnimationStep = (props: UseAnimationStepProps): UseAnimationStep => {
  const anim = useAnimationFrame();
  const current = refWithControl<number>(props.initial || 0.0);
  const isRunning = ref<boolean>(false);

  const stop = () => {
    anim.stop();
  };

  const set = (value: number) => {
    stop();
    current.value = value;
  }

  const defaultCurveFunction: CurveFunction = (start: number, target: number, mix: number): number => {
    return lerp(start, target, mix);
  }

  const runCallback = (value: number): number => {
    if (props.callback) {
      props.callback(value);
    }
    return value;
  }

  const to = (target: number, duration: number = (props.duration ?? 1.0), curve: CurveFunction = (props.curve || defaultCurveFunction)) => {
    isRunning.value = true;
    const vFrom = current.peek();
    return new Promise<void>((resolve) => {
      anim.run((ctx) => {
        if (ctx.elapsed > duration) {
          current.value = runCallback(target);
          isRunning.value = false;
          ctx.stop();
          resolve();
        } else {
          current.value = runCallback(curve(vFrom, target, clamp(ctx.elapsed / duration, 0.0, 1.0)));
        }
      });
    });
  };

  return {
    current,
    stop,
    to,
    set,
    running: isRunning
  };
};
